import React, { useContext, useEffect, useState } from 'react';

import { Text, colors } from '@prenuvo/shared-library';
import styled from 'styled-components';
import { BrowserRouter, Route, Switch, useRouteMatch } from 'react-router-dom';
import { StudyContext } from 'packages/booking-contexts';
import data from './SafetyQuestions.json';
import dataUK from './SafetyQuestions-v1.2.json';
import QuestionRow from './QuestionRow';
import {
  getRequiredQuestions,
  checkIfShownWhenTrue as showWhenTrue,
} from '../safetyQuestionFunctions';

const QuestionSection = styled.div<{ grey: boolean; clear: boolean }>`
  ${({ grey, clear }) =>
    grey && !clear
      ? 'background: rgba(242, 243, 247, 0.6)'
      : `background-color: ${clear && grey ? 'none' : colors.white}`};
`;

interface SafetyQuestionsProps {
  studyID: string;
  piiContext: any;
  checkinContext: any;
  updateCheckinContext: () => void;
  getRedux: (studyID) => void;
  setRequiredFields: (nestedQuestions: any[]) => void;
  skuID?: string;
}

const SafetyQuestions: React.FC<SafetyQuestionsProps> = ({
  studyID,
  piiContext,
  checkinContext,
  updateCheckinContext,
  getRedux,
  setRequiredFields,
  skuID,
}: SafetyQuestionsProps) => {
  const { path } = useRouteMatch();
  const studyContext = useContext(StudyContext) as { machineID?: string } | undefined;
  const machineID = studyContext?.machineID;
  const [safetyQuestions, setSafetyQuestions] = useState(() => {
    // Initialize based on machineID if available, otherwise return empty array
    if (machineID == null || machineID === '') {
      return [];
    }
    if (machineID.includes('eng-')) {
      return dataUK?.subs || [];
    }
    return data?.subs || [];
  });

  useEffect(() => {
    if (studyID) {
      getRedux(studyID);
    }
  }, [studyID]);

  useEffect(() => {
    // Update when machineID changes (e.g., when it loads asynchronously or user selects different machine)
    if (machineID == null || machineID === '') {
      setSafetyQuestions([]);
    } else if (machineID.includes('eng-')) {
      setSafetyQuestions(dataUK?.subs || []);
    } else {
      setSafetyQuestions(data?.subs || []);
    }
  }, [machineID]);

  const checkIfShownWhenTrue = (question) => {
    return showWhenTrue({ question, checkinContext, piiContext });
  };

  useEffect(() => {
    const nestedQuestions = getRequiredQuestions({ checkinContext, piiContext, safetyQuestions });
    setRequiredFields(nestedQuestions);
  }, [checkinContext, safetyQuestions]);

  return (
    <>
      <Text weight="600">Please answer the following safety questions:</Text>
      {safetyQuestions.map((question, index) => (
        <QuestionSection
          grey={index % 2 === 0}
          clear={path === '/admin/booking'}
          key={`${question.field}-section`}
        >
          <QuestionRow
            question={question}
            checkinContext={checkinContext}
            piiContext={piiContext}
            updateCheckinContext={updateCheckinContext}
            checkIfShownWhenTrue={checkIfShownWhenTrue}
            readOnly={skuID === '900'}
            key={`${question.field}-row`}
          />
        </QuestionSection>
      ))}
    </>
  );
};

const SafetyQuestionsWithRouter = (props) => (
  <BrowserRouter>
    <Switch>
      <Route path="/admin/booking" render={() => <SafetyQuestions {...props} />} />
      <Route path="/admin/frontdesk" render={() => <SafetyQuestions {...props} />} />
      <Route path="/booking/referral" render={() => <SafetyQuestions {...props} />} />
    </Switch>
  </BrowserRouter>
);

export default SafetyQuestionsWithRouter;
